import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { IconArrowRight, IconEye, IconEyeOff } from '@tabler/icons-react';
import { Button } from '@planne-software/uni/Button';
import { Input } from '@planne-software/uni/Input';

import { useTranslate } from '@src/core/hooks';
import {
  AuthCardButtonWrapper,
  AuthCardRoot,
  AuthCardTextGroup,
  AuthCardTitle,
  ButtonGroup,
  PasswordSuffixButton,
} from '@src/features/customers';

import { useRecoveryNewPasswordForm } from '../hooks/useRecoveryNewPasswordForm';
import { PasswordFieldStack } from '../styles';

type PasswordRecoveryNewPasswordCardProps = {
  onBack: () => void;
};

export const PasswordRecoveryNewPasswordCard = (props: PasswordRecoveryNewPasswordCardProps) => {
  const { onBack } = props;
  const { i18n } = useTranslate();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmationVisible, setConfirmationVisible] = useState(false);

  const { control, errors, loading, isSubmitEnabled, onSubmit, passwordSuccess, confirmationSuccess } =
    useRecoveryNewPasswordForm();

  return (
    <AuthCardRoot data-testid='password-recovery-new-password-card'>
      <AuthCardTextGroup>
        <AuthCardTitle>{i18n('checkout_auth_recovery_new_password_title')}</AuthCardTitle>
      </AuthCardTextGroup>

      <form onSubmit={onSubmit} noValidate>
        <AuthCardRoot>
          <PasswordFieldStack>
            <Controller
              name='password'
              control={control}
              rules={{ deps: 'passwordConfirmation' }}
              render={({ field: { onChange, onBlur, value, name, ref, ...rest } }) => (
                <Input
                  type={passwordVisible ? 'text' : 'password'}
                  label={i18n('checkout_auth_new_password_label')}
                  name={name}
                  id={name}
                  value={value ?? ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors.password}
                  success={passwordSuccess}
                  disabled={loading}
                  helperText={errors.password?.message ?? i18n('checkout_auth_password_min_length_helper')}
                  autoComplete='new-password'
                  ref={ref}
                  size='medium'
                  suffixSlot={
                    <PasswordSuffixButton
                      type='button'
                      onClick={() => setPasswordVisible((prev) => !prev)}
                      aria-label={
                        passwordVisible ? i18n('checkout_auth_hide_password') : i18n('checkout_auth_show_password')
                      }
                    >
                      {passwordVisible ? <IconEyeOff size={20} aria-hidden /> : <IconEye size={20} aria-hidden />}
                    </PasswordSuffixButton>
                  }
                  {...rest}
                />
              )}
            />
          </PasswordFieldStack>

          <PasswordFieldStack>
            <Controller
              name='passwordConfirmation'
              control={control}
              render={({ field: { onChange, onBlur, value, name, ref, ...rest } }) => (
                <Input
                  type={confirmationVisible ? 'text' : 'password'}
                  label={i18n('checkout_auth_confirm_password_label')}
                  name={name}
                  id={name}
                  value={value ?? ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors.passwordConfirmation}
                  success={confirmationSuccess}
                  disabled={loading}
                  helperText={errors.passwordConfirmation?.message ?? i18n('checkout_auth_password_repeat_helper')}
                  autoComplete='new-password'
                  ref={ref}
                  size='medium'
                  suffixSlot={
                    <PasswordSuffixButton
                      type='button'
                      onClick={() => setConfirmationVisible((prev) => !prev)}
                      aria-label={
                        confirmationVisible ? i18n('checkout_auth_hide_password') : i18n('checkout_auth_show_password')
                      }
                    >
                      {confirmationVisible ? <IconEyeOff size={20} aria-hidden /> : <IconEye size={20} aria-hidden />}
                    </PasswordSuffixButton>
                  }
                  {...rest}
                />
              )}
            />
          </PasswordFieldStack>

          <ButtonGroup>
            <AuthCardButtonWrapper>
              <Button
                type='submit'
                fullWidth
                loading={loading}
                disabled={!isSubmitEnabled}
                endSlot={<IconArrowRight size={20} aria-hidden />}
              >
                {i18n('checkout_auth_reset_password_cta')}
              </Button>
            </AuthCardButtonWrapper>

            <AuthCardButtonWrapper>
              <Button type='button' variant='slim' fullWidth disabled={loading} onClick={onBack}>
                {i18n('checkout_auth_back_cta')}
              </Button>
            </AuthCardButtonWrapper>
          </ButtonGroup>
        </AuthCardRoot>
      </form>
    </AuthCardRoot>
  );
};

export default PasswordRecoveryNewPasswordCard;
